<template>
  <v-card flat class="bmp-code-editor codemirror pa-0 ma-0" data-cy="card">
    <codemirror v-bind="$attrs" ref="cm" v-model="model" :options="cmOption" data-cy="codeEditor"></codemirror>
  </v-card>
</template>

<script>
import lib, { codemirror } from 'vue-codemirror'

// base style
import 'codemirror/lib/codemirror.css'
import 'codemirror/addon/display/fullscreen.css'

// theme css
import 'codemirror/theme/base16-dark.css'

// language
import 'codemirror/mode/xml/xml.js'
import 'codemirror/mode/vue/vue.js'
import 'codemirror/mode/javascript/javascript.js'

import 'codemirror/addon/display/autorefresh.js'
// active-line.js
import 'codemirror/addon/selection/active-line.js'

// styleSelectedText
import 'codemirror/addon/selection/mark-selection.js'
import 'codemirror/addon/search/searchcursor.js'

// highlightSelectionMatches
import 'codemirror/addon/scroll/annotatescrollbar.js'
import 'codemirror/addon/search/matchesonscrollbar.js'

import 'codemirror/addon/search/match-highlighter.js'

// keyMap
import 'codemirror/mode/clike/clike.js'
import 'codemirror/addon/edit/matchbrackets.js'
import 'codemirror/addon/comment/comment.js'
import 'codemirror/addon/dialog/dialog.js'
import 'codemirror/addon/dialog/dialog.css'

import 'codemirror/addon/search/search.js'
import 'codemirror/keymap/sublime.js'

// foldGutter
import 'codemirror/addon/fold/foldgutter.css'
import 'codemirror/addon/fold/brace-fold.js'
import 'codemirror/addon/fold/comment-fold.js'
import 'codemirror/addon/fold/foldcode.js'
import 'codemirror/addon/fold/foldgutter.js'
import 'codemirror/addon/fold/indent-fold.js'
import 'codemirror/addon/fold/markdown-fold.js'
import 'codemirror/addon/fold/xml-fold.js'

import 'codemirror/addon/display/fullscreen.js'

// require more codemirror resource...

const CodeMirror = lib.CodeMirror
var inlineElements = /^(a|abbr|acronym|area|base|bdo|big|br|button|caption|cite|code|col|colgroup|dd|del|dfn|em|frame|hr|iframe|img|input|ins|kbd|label|legend|link|map|object|optgroup|option|param|q|samp|script|select|small|span|strong|sub|sup|textarea|tt|var)$/

// component
export default {
  components: {
    codemirror
  },
  props: {
    value: null,
    mode: { type: String, default: 'text/javascript' }
  },
  data () {
    return {
      cmOption: Object.assign({
        tabSize: 2,
        styleActiveLine: true,
        lineNumbers: true,
        line: true,
        autoRefresh: true,

        keyMap: 'sublime',
        mode: this.mode,
        theme: 'base16-dark',
        foldGutter: true,
        gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
        foldOptions: {
          widget: (from, to) => {
            var count = null

            // Get open / close token
            var startToken = '{'
            var endToken = '}'
            var prevLine = this.codemirror.getLine(from.line)
            if (prevLine.lastIndexOf('[') > prevLine.lastIndexOf('{')) {
              startToken = '['
              endToken = ']'
            }

            // Get json content
            var internal = this.codemirror.getRange(from, to)
            var toParse = startToken + internal + endToken

            // Get key count
            try {
              var parsed = JSON.parse(toParse)
              count = Object.keys(parsed).length
            } catch (e) {}

            return count ? `\u21A4${count}\u21A6` : '\u2194'
          }
        },
        extraKeys: {
          F11 (cm) {
            cm.setOption('fullScreen', !cm.getOption('fullScreen'))
          },
          Esc (cm) {
            if (cm.getOption('fullScreen')) cm.setOption('fullScreen', false)
          },
          'Ctrl-S' (cm) {
            cm.executeSaveCommand()
          }
        }
      })
    }
  },
  computed: {
    model: {
      get () {
        return this.value === undefined || this.value === null ? '' : this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    codemirror () {
      // eslint-disable-next-line vue/no-async-in-computed-properties
      setTimeout(() => {
        this.$refs.cm.codemirror.refresh()

        this.$refs.cm.codemirror.customSave = () => true
        if (this.$listeners && this.$listeners.save) {
          this.codemirror.customSave = this.$listeners.save
        }
        CodeMirror.defineExtension('executeSaveCommand', function (from, to) {
          const cm = this
          cm.customSave()
        })
      }, 200)
      return this.$refs.cm.codemirror
    }
  },
  methods: {
    indent () {
      this.model = this.model
        .replace(/ {2}/gm, ' ')
        .replace(/(" >)/gm, '">')
        .replace(/( >)/gm, '>')
      this.codemirror.execCommand('selectAll')
      var range = {
        from: this.codemirror.getCursor(true),
        to: this.codemirror.getCursor(false)
      }
      this.codemirror.autoFormatRange(range.from, range.to)
      this.codemirror.setCursor(0)
    }
  },
  mounted () {
    var vm = this

    setTimeout(() => {
      vm.styleSelectedText = true
      vm.cmOption.styleActiveLine = true
    }, 1800)

    if (vm.$attrs.height) {
      vm.codemirror.setSize(null, vm.$attrs.height)
    }

    CodeMirror.extendMode('css', {
      commentStart: '/*',
      commentEnd: '*/',
      newlineAfterToken: function (type, content) {
        return /^[;{}]$/.test(content)
      }
    })

    CodeMirror.extendMode('javascript', {
      commentStart: '/*',
      commentEnd: '*/',
      // FIXME semicolons inside of for
      newlineAfterToken: function (type, content, textAfter, state) {
        if (this.jsonMode) {
          // eslint-disable-next-line no-useless-escape
          return /^[\[,{]$/.test(content) || /^}/.test(textAfter)
        } else {
          if (content === ';' && state.lexical && state.lexical.type === ')') { return false }
          return /^[;{}]$/.test(content) && !/^;/.test(textAfter)
        }
      }
    })

    CodeMirror.extendMode('xml', {
      commentStart: '<!--',
      commentEnd: '-->',
      newlineAfterToken: function (type, content, textAfter, state) {
        var inline = false
        if (this.configuration === 'html') {
          inline = state.context
            ? inlineElements.test(state.context.tagName)
            : false
        }
        return (
          !inline &&
          ((type === 'tag' && />$/.test(content) && state.context) ||
            /^</.test(textAfter))
        )
      }
    })

    // Comment/uncomment the specified range
    CodeMirror.defineExtension('commentRange', function (isComment, from, to) {
      var cm = this
      var curMode = CodeMirror.innerMode(
        cm.getMode(),
        cm.getTokenAt(from).state
      ).mode
      cm.operation(function () {
        if (isComment) {
          // Comment range
          cm.replaceRange(curMode.commentEnd, to)
          cm.replaceRange(curMode.commentStart, from)
          // An empty comment inserted - put cursor inside
          if (from.line === to.line && from.ch === to.ch) {
            cm.setCursor(from.line, from.ch + curMode.commentStart.length)
          }
        } else {
          // Uncomment range
          var selText = cm.getRange(from, to)
          var startIndex = selText.indexOf(curMode.commentStart)
          var endIndex = selText.lastIndexOf(curMode.commentEnd)
          if (startIndex > -1 && endIndex > -1 && endIndex > startIndex) {
            // Take string till comment start
            selText =
              selText.substr(0, startIndex) +
              // From comment start till comment end
              selText.substring(
                startIndex + curMode.commentStart.length,
                endIndex
              ) +
              // From comment end till string end
              selText.substr(endIndex + curMode.commentEnd.length)
          }
          cm.replaceRange(selText, from, to)
        }
      })
    })

    // Applies automatic mode-aware indentation to the specified range
    CodeMirror.defineExtension('autoIndentRange', function (from, to) {
      var cmInstance = this
      this.operation(function () {
        for (var i = from.line; i <= to.line; i++) {
          cmInstance.indentLine(i, 'smart')
        }
      })
    })

    // Applies automatic formatting to the specified range
    CodeMirror.defineExtension('autoFormatRange', function (from, to) {
      var cm = this
      var outer = cm.getMode()
      var text = cm.getRange(from, to).split('\n')
      var state = CodeMirror.copyState(outer, cm.getTokenAt(from).state)
      var tabSize = cm.getOption('tabSize')

      var out = ''
      var lines = 0
      var atSol = from.ch === 0
      function newline () {
        out += '\n'
        atSol = true
        ++lines
      }

      for (var i = 0; i < text.length; ++i) {
        var stream = new CodeMirror.StringStream(text[i], tabSize)
        while (!stream.eol()) {
          var inner = CodeMirror.innerMode(outer, state)
          var style = outer.token(stream, state)
          var cur = stream.current()
          stream.start = stream.pos
          if (!atSol || /\S/.test(cur)) {
            out += cur
            atSol = false
          }
          if (
            !atSol &&
            inner.mode.newlineAfterToken &&
            inner.mode.newlineAfterToken(
              style,
              cur,
              stream.string.slice(stream.pos) || text[i + 1] || '',
              inner.state
            )
          ) {
            newline()
          }
        }
        if (!stream.pos && outer.blankLine) outer.blankLine(state)
        if (!atSol) newline()
      }

      cm.operation(function () {
        cm.replaceRange(out, from, to)
        for (
          var cur = from.line + 1, end = from.line + lines;
          cur <= end;
          ++cur
        ) {
          cm.indentLine(cur, 'smart')
        }
        cm.setSelection(from, cm.getCursor(false))
      })
    })
  }
}
</script>

<style>
.bmp-code-editor .CodeMirror {
  height: auto;
}

.bmp-code-editor .CodeMirror pre.CodeMirror-line {
  padding-left: 20px;
}

.bmp-code-editor .CodeMirror-sizer {
  min-height: 200px !important;
}
</style>
